import { api } from "@/client/api";
import { CardListView } from "@/client/components/card-list-view";
import { CustomLoader } from "@/client/components/loaders";
import { SearchFormView } from "@/client/components/search-form";
import { useUserCards } from "@/client/hooks";
import { DEFAULT_FORM_VALUES, useStore } from "@/client/store";
import { FiltersSchema, SortMethods } from "@/server/api/routers/schemas";
import { useCreditCards } from "@/server/composables/useCreditCards";
import { getCardValue, getPercentReturn } from "@/utils/calculations";
import { getIneligibleCards, type CardReason } from "@/utils/eligibility";
import { cardMatchesFilters } from "@/utils/filter";
import { useAuth } from "@clerk/nextjs";
import {
  Button,
  Container,
  Grid,
  Group,
  Stack,
  Text,
  Title,
} from "@mantine/core";
// Test change
import { useForm } from "@mantine/form";
import type { NextPage } from "next";
import { useEffect, useMemo, useRef, useState } from "react";

const PAGE_SIZE = 20;

const Home: NextPage = () => {
  const { filters, setFilters } = useStore();
  const auth = useAuth();
  const updateFilters = api.filters.update.useMutation();

  const userFilters = api.filters.get.useQuery(undefined, {
    enabled: auth.isSignedIn ?? false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: 1000 * 60 * 60 * 24,
  });
  const userCards = useUserCards();

  const [page, setPage] = useState(1);

  const form = useForm({ initialValues: DEFAULT_FORM_VALUES });

  /* Prevents the save effect from firing with stale DEFAULT_FORM_VALUES in
   * the same render cycle that server data first arrives. React effects run
   * in declaration order, so the load effect sets this flag before the save
   * effect checks it, letting us skip the spurious overwrite. */
  const isInitializingFromServer = useRef(false);

  useEffect(() => {
    //* Load values from server
    if (userFilters.data) {
      console.debug(
        `Loaded form values from server: ${JSON.stringify(userFilters.data, null, 2)}`,
      );
      isInitializingFromServer.current = true;
      form.setValues(JSON.parse(userFilters.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form.setValues is stable; including form object causes infinite re-render loop
  }, [userFilters.data]);

  useEffect(() => {
    //* Process form value changes
    if (isInitializingFromServer.current) {
      /* form.values is still stale defaults at this point — setValues hasn't
       * committed yet. Skip the save and let the next render handle it. */
      isInitializingFromServer.current = false;
      return;
    }
    console.debug("New form values", form.values);
    try {
      FiltersSchema.parse(form.values);
    } catch (error) {
      console.error("Invalid filters:", form.values, error);
      return;
    }

    setFilters(form.values);
    if (
      userFilters.isSuccess &&
      userFilters.data !==
        JSON.stringify(
          form.values,
        ) /* userFilters.data is already a JSON string from the DB, so no need to stringify it */
    ) {
      updateFilters.mutate({ filters: JSON.stringify(form.values) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Zustand store methods are stable; including them causes infinite loops
  }, [form.values, userFilters.isSuccess, userFilters.data]);

  const creditCards = useCreditCards();
  const [ineligibleCards, setIneligibleCards] = useState<CardReason[]>([]);
  useEffect(() => {
    const refresh = async () => {
      if (!userCards.data || creditCards.length === 0) return;
      const data = await getIneligibleCards(userCards.data, creditCards);
      setIneligibleCards(data);
    };
    refresh().catch((error) => console.error(error));
  }, [creditCards, userCards.data]);

  const cards = useMemo(() => {
    //* Recalculate cards based on filters
    return creditCards
      .filter((card) => cardMatchesFilters(card, filters))
      .filter((card) => {
        const filteredIneligibleCards = ineligibleCards.filter(
          (reason: CardReason) => reason.reasons.length,
        );
        return !filteredIneligibleCards.some(
          (reason: CardReason) =>
            reason.card.name === card.name &&
            reason.card.issuer === card.issuer,
        );
      });
  }, [creditCards, filters, ineligibleCards]);

  /**
   * Done here rather than above callback b/c it should NOT happen on creditCards or downstream change,
   * otherwise we reset the page anytime they leave and come back to the page
   */
  useEffect(() => setPage(1), [filters]);

  const cardsAfterSortAndPageTruncate = useMemo(() => {
    return cards
      .sort((cardA, cardB) => {
        if (!filters.sortMethod) filters.sortMethod = SortMethods.ByValue;
        if (filters.sortMethod === SortMethods.ByValue) {
          const aValue = getCardValue(cardA);
          const bValue = getCardValue(cardB);
          return bValue - aValue;
        }
        if (filters.sortMethod === SortMethods.ByPercentReturn) {
          if (cardA.offers.length === 0) return 1;
          if (cardB.offers.length === 0) return -1;
          const maxPercentReturnOfferForCardA = cardA.offers.reduce(
            (maxOffer, offer) => {
              // TODO: There's a bit of recalculation here that can be optimized, but magnitude should be small enough that it's not ever worth doing
              const percentReturn = getPercentReturn(offer, cardA);
              if (percentReturn > getPercentReturn(maxOffer, cardA))
                return offer;
              return maxOffer;
            },
            cardA.offers[0],
          );
          const maxPercentReturnOfferForCardB = cardB.offers.reduce(
            (maxOffer, offer) => {
              // TODO: There's a bit of recalculation here that can be optimized, but magnitude should be small enough that it's not ever worth doing
              const percentReturn = getPercentReturn(offer, cardA);
              if (percentReturn > getPercentReturn(maxOffer, cardA))
                return offer;
              return maxOffer;
            },
            cardB.offers[0],
          );
          return (
            getPercentReturn(maxPercentReturnOfferForCardB, cardB) -
            getPercentReturn(maxPercentReturnOfferForCardA, cardA)
          );
        }
        throw new Error(`Unrecognized sort type: ${filters.sortMethod}`);
      })
      .slice(0, page * PAGE_SIZE);
  }, [cards, filters, page]);

  return (
    <Grid
      style={{
        maxHeight: "calc(100vh - 50px)",
      }}
    >
      <Grid.Col
        span={{ lg: 3, sm: 4 }}
        style={{
          maxHeight: "calc(100vh - 50px)",
          overflowY: "scroll",
          borderRight: "2px var(--app-shell-border-color) solid",
        }}
      >
        <SearchFormView form={form} ineligibleCards={ineligibleCards} />
      </Grid.Col>
      <Grid.Col
        style={{
          maxHeight: "calc(100vh - 50px)",
          overflowY: "scroll",
        }}
        span={{ lg: 9, sm: 8 }}
      >
        <Container size={"lg"} pt={"sm"}>
          <Stack>
            <Title order={3}>
              Credit Cards ({cards.length}/{creditCards.length})
            </Title>
            {creditCards.length === 0 && (
              <Group>
                <CustomLoader />
                <Text>Loading Cards...</Text>
              </Group>
            )}
            <CardListView cards={cardsAfterSortAndPageTruncate} />
            {cards.length > 0 && cards.length > page * PAGE_SIZE && (
              <Button
                mt={"sm"}
                fullWidth={true}
                variant={"gradient"}
                onClick={() => setPage(page + 1)}
              >
                Show More
              </Button>
            )}
          </Stack>
        </Container>
      </Grid.Col>
    </Grid>
  );
};
export default Home;
